import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { callZohoAPI } from '../utils/api';
import { Sparkles } from 'lucide-react';
import { discoverRelatedListName } from '../services/crmApi';
const modules = [{
  label: 'Contacts',
  value: 'Contacts'
}, {
  label: 'Tags',
  value: 'Tags'
}, {
  label: 'Categories',
  value: 'Categories'
}, {
  label: 'Mailing Lists',
  value: 'Mailing_Lists'
}, {
  label: '<Custom>',
  value: '__custom__'
}];
const moduleDefaultFields: Record<string, string> = {
  Contacts: 'id,Email,First_Name,Last_Name,Full_Name,Tags,Region,Sub_Region,Team,Bucket_ID,Bucket_ID_Expires_At,Status,Form_Name,Phone,Mobile,Created_Time,Modified_Time,Owner,Title,Department',
  Tags: 'id,Name,Team,Region,Created_Time,Modified_Time',
  Categories: 'id,Name,Description,Team,Region,Created_Time,Modified_Time',
  Mailing_Lists: 'id,Name,Description,Status,Subscriber_Count,Team,Region,Sub_Region,Created_Time,Modified_Time'
};
const relatedModules = [{
  label: 'Notes',
  value: 'Notes'
}, {
  label: 'Categories',
  value: 'Categories14'
}, {
  label: 'Mailing Lists',
  value: 'Mailing_Lists3'
}, {
  label: 'Tags',
  value: 'Tags1'
}, {
  label: 'Contacts',
  value: 'Contacts'
}];
const CONTACT_FIELDS_FOR_TEST = 'id,Email,First_Name,Last_Name,Full_Name,Tags,Region,Sub_Region,Team,Bucket_ID,Status,Form_Name,Phone';
const tabs = [{
  id: 'list',
  label: 'List Records'
}, {
  id: 'details',
  label: 'Record Details'
}, {
  id: 'reverse',
  label: 'Reverse Lookup'
}, {
  id: 'settings',
  label: 'Settings'
}] as const;
type TabId = (typeof tabs)[number]['id'];
function ResponseBlock({
  response,
  error,
  onClear
}: {
  response: any;
  error: string | null;
  onClear: () => void;
}) {
  return <>
      {error && <div className="bg-red-50 border border-red-200 rounded-lg p-4" data-id="element-2341">
          <p className="text-sm font-medium text-red-800" data-id="element-2342">Error</p>
          <p className="text-sm text-red-700 mt-1 font-mono" data-id="element-2343">{error}</p>
        </div>}
      {response && <div data-id="element-2344">
          <div className="flex items-center justify-between mb-2" data-id="element-2345">
            <p className="text-sm font-medium text-gray-700" data-id="element-2346">
              Response
              {response?.info?.count != null && <span className="ml-2 text-xs text-gray-500" data-id="element-2347">
                  ({response.info.count} records, page {response.info.page},
                  more: {String(response.info.more_records)})
                </span>}
            </p>
            <button onClick={onClear} className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-2348">
              Clear
            </button>
          </div>
          <div className="bg-gray-900 rounded-lg p-5 overflow-auto max-h-[70vh]" data-id="element-2349">
            <pre className="text-sm text-green-400 font-mono whitespace-pre-wrap break-words" data-id="element-2350">
              {JSON.stringify(response, null, 2)}
            </pre>
          </div>
        </div>}
    </>;
}
export function ApiTest() {
  const [selectedModule, setSelectedModule] = useState('Contacts');
  const [customModule, setCustomModule] = useState('');
  const [activeTab, setActiveTab] = useState<TabId>('list');
  const [loading, setLoading] = useState<string | null>(null);
  const [customFields, setCustomFields] = useState('');
  // Tab 1 state
  const [listResponse, setListResponse] = useState<any>(null);
  const [listError, setListError] = useState<string | null>(null);
  // Tab 2 state
  const [detailResponse, setDetailResponse] = useState<any>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [recordId, setRecordId] = useState('');
  const [relatedModule, setRelatedModule] = useState('Notes');
  const [relatedFields, setRelatedFields] = useState('ID');
  // Tab 3 (Reverse Lookup) state
  const [reverseResponse, setReverseResponse] = useState<any>(null);
  const [reverseError, setReverseError] = useState<string | null>(null);
  const [reverseParentModule, setReverseParentModule] = useState('Mailing_Lists');
  const [reverseParentId, setReverseParentId] = useState('');
  const [reverseChildModule, setReverseChildModule] = useState('Contacts');
  const [discoveredApiName, setDiscoveredApiName] = useState<string | null>(null);
  const [discoveryLog, setDiscoveryLog] = useState<string>('');
  // Tab 4 state
  const [settingsResponse, setSettingsResponse] = useState<any>(null);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const effectiveModule = selectedModule === '__custom__' ? customModule.trim() : selectedModule;
  const moduleLabel = selectedModule === '__custom__' ? customModule.trim() || 'Custom' : modules.find(m => m.value === selectedModule)?.label || selectedModule;
  const effectiveFields = customFields.trim() || moduleDefaultFields[effectiveModule] || '';
  const fieldsParam = effectiveFields ? `fields=${effectiveFields}&` : '';
  const listUrl = `crm/v8/${effectiveModule}?${fieldsParam}per_page=200`;
  const detailUrl = `crm/v8/${effectiveModule}/${recordId.trim() || '{ID}'}`;
  const relatedUrl = `crm/v8/${effectiveModule}/${recordId.trim() || '{ID}'}/${relatedModule}?fields=${relatedFields.trim() || 'ID'}`;
  const relatedListsUrl = `crm/v8/settings/related_lists?module=${effectiveModule}`;
  const fieldsUrl = `crm/v8/settings/fields?module=${effectiveModule}`;
  const handleFetchList = async () => {
    if (!effectiveModule) return;
    setLoading('list');
    setListError(null);
    setListResponse(null);
    try {
      const data = await callZohoAPI(listUrl);
      setListResponse(data);
    } catch (err) {
      setListError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(null);
    }
  };
  const handleFetchDetails = async () => {
    if (!recordId.trim() || !effectiveModule) return;
    setLoading('detail');
    setDetailError(null);
    setDetailResponse(null);
    try {
      const data = await callZohoAPI(`crm/v8/${effectiveModule}/${recordId.trim()}`);
      setDetailResponse(data);
    } catch (err) {
      setDetailError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(null);
    }
  };
  const handleFetchRelated = async () => {
    if (!recordId.trim() || !effectiveModule) return;
    setLoading('related');
    setDetailError(null);
    setDetailResponse(null);
    try {
      const data = await callZohoAPI(`crm/v8/${effectiveModule}/${recordId.trim()}/${relatedModule}?fields=${relatedFields.trim() || 'ID'}`);
      setDetailResponse(data);
    } catch (err) {
      setDetailError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(null);
    }
  };
  const handleDiscoverAndReverseLookup = async () => {
    if (!reverseParentId.trim()) return;
    setLoading('reverse');
    setReverseError(null);
    setReverseResponse(null);
    setDiscoveryLog('');
    setDiscoveredApiName(null);
    try {
      // Step 1: Discover the correct related list API name using v8 Settings API
      setDiscoveryLog('Step 1: Discovering related list API name (v8 API)...');
      // Check hardcoded names first
      const hardcodedNames: Record<string, string> = {
        'Mailing_Lists:Contacts': 'Contacts5',
        'Categories:Contacts': 'Contacts4'
      };
      const hardcodedKey = `${reverseParentModule}:${reverseChildModule}`;
      const hardcodedName = hardcodedNames[hardcodedKey];
      if (hardcodedName) {
        setDiscoveryLog(prev => prev + `\n✓ Using known API name: "${hardcodedName}" (from CRM Settings > API Names)`);
        setDiscoveredApiName(hardcodedName);
        // Step 2: Fetch related records using the known name with v8 API
        // The `fields` parameter is MANDATORY for the v8 Related Records API
        setDiscoveryLog(prev => prev + `\n\nStep 2: Fetching via crm/v8/${reverseParentModule}/${reverseParentId.trim()}/${hardcodedName}?fields=${reverseChildModule},Email,Name...`);
        const url = `crm/v8/${reverseParentModule}/${reverseParentId.trim()}/${hardcodedName}?fields=${reverseChildModule},Email,Name,Owner&per_page=200`;
        const data = await callZohoAPI(url);
        const records = data?.data || [];
        const count = records.length;
        // Extract actual contact IDs from junction records
        // Junction records have a lookup field (e.g., "Contacts": {name, id}) pointing to the real record
        const contactIds: string[] = [];
        for (const record of records) {
          // Look for the child module lookup field
          const lookupField = record[reverseChildModule];
          if (lookupField && typeof lookupField === 'object' && lookupField.id) {
            contactIds.push(lookupField.id);
          }
        }
        if (contactIds.length > 0) {
          setDiscoveryLog(prev => prev + `\n✓ Success! Found ${count} junction record(s) → ${contactIds.length} actual ${reverseChildModule} IDs` + `\n\nExtracted ${reverseChildModule} IDs:\n  ${contactIds.join('\n  ')}`);
        } else {
          setDiscoveryLog(prev => prev + `\n✓ Success! Found ${count} record(s)`);
        }
        setReverseResponse(data);
        return;
      }
      // No hardcoded name — discover via v8 Settings API
      const settingsResponse = await callZohoAPI(`crm/v8/settings/related_lists?module=${reverseParentModule}`);
      const relatedLists = settingsResponse?.related_lists || [];
      // Strategy 1: Match by module.api_name
      let matches = relatedLists.filter((rl: any) => rl.module?.api_name === reverseChildModule);
      // Strategy 2: Match by display name
      if (matches.length === 0) {
        matches = relatedLists.filter((rl: any) => rl.name === reverseChildModule || rl.name === reverseChildModule.replace(/_/g, ' '));
      }
      // Strategy 3: Match by api_name pattern (e.g., "Contacts5" starts with "Contacts")
      if (matches.length === 0) {
        const pattern = new RegExp(`^${reverseChildModule}\\d*$`);
        matches = relatedLists.filter((rl: any) => pattern.test(rl.api_name));
      }
      const matchInfo = matches.map((m: any) => `"${m.api_name}" (name: "${m.name}", type: ${m.type || 'unknown'}, module: ${m.module?.api_name || 'unknown'})`).join(', ');
      setDiscoveryLog(prev => prev + `\nFound ${matches.length} relation(s): ${matchInfo || 'none'}`);
      if (matches.length === 0) {
        const allNames = relatedLists.map((rl: any) => `"${rl.api_name}" → ${rl.module?.api_name || 'unknown'} (${rl.name})`).join('\n  ');
        setDiscoveryLog(prev => prev + `\n\n⚠ No "${reverseChildModule}" relation found! All available relations:\n  ${allNames}`);
        setReverseError(`No "${reverseChildModule}" relation found on ${reverseParentModule}. Check the discovery log for available relations.`);
        return;
      }
      // Pick best match
      let bestMatch = matches[0];
      for (const match of matches) {
        if (match.type === 'multiselectlookup') {
          bestMatch = match;
          break;
        }
        if (/\d+$/.test(match.api_name) && !/\d+$/.test(bestMatch.api_name)) {
          bestMatch = match;
        }
      }
      const apiName = bestMatch.api_name;
      setDiscoveredApiName(apiName);
      setDiscoveryLog(prev => prev + `\n✓ Using: "${apiName}"`);
      // Step 2: Fetch related records using v8 API
      setDiscoveryLog(prev => prev + `\n\nStep 2: Fetching ${reverseChildModule} via crm/v8/${reverseParentModule}/${reverseParentId.trim()}/${apiName}?fields=${reverseChildModule},Email,Name...`);
      const url = `crm/v8/${reverseParentModule}/${reverseParentId.trim()}/${apiName}?fields=${reverseChildModule},Email,Name,Owner&per_page=200`;
      const data = await callZohoAPI(url);
      const records = data?.data || [];
      const count = records.length;
      // Extract actual contact IDs from junction records
      const contactIds: string[] = [];
      for (const record of records) {
        const lookupField = record[reverseChildModule];
        if (lookupField && typeof lookupField === 'object' && lookupField.id) {
          contactIds.push(lookupField.id);
        }
      }
      if (contactIds.length > 0) {
        setDiscoveryLog(prev => prev + `\n✓ Success! Found ${count} junction record(s) → ${contactIds.length} actual ${reverseChildModule} IDs` + `\n\nExtracted ${reverseChildModule} IDs:\n  ${contactIds.join('\n  ')}`);
      } else {
        setDiscoveryLog(prev => prev + `\n✓ Success! Found ${count} record(s)`);
      }
      setReverseResponse(data);
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : 'Unknown error';
      setDiscoveryLog(prev => prev + `\n✗ Error: ${errMsg}`);
      setReverseError(errMsg);
    } finally {
      setLoading(null);
    }
  };
  const handleFetchRelatedLists = async () => {
    if (!effectiveModule) return;
    setLoading('relatedLists');
    setSettingsError(null);
    setSettingsResponse(null);
    try {
      const data = await callZohoAPI(`crm/v8/settings/related_lists?module=${effectiveModule}`);
      setSettingsResponse(data);
    } catch (err) {
      setSettingsError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(null);
    }
  };
  const handleFetchFields = async () => {
    if (!effectiveModule) return;
    setLoading('fields');
    setSettingsError(null);
    setSettingsResponse(null);
    try {
      const data = await callZohoAPI(`crm/v8/settings/fields?module=${effectiveModule}`);
      setSettingsResponse(data);
    } catch (err) {
      setSettingsError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(null);
    }
  };
  return <div className="space-y-6" data-id="element-2351">
      <div className="flex items-center justify-between" data-id="element-2352">
        <div data-id="element-2353">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-2354">
            Zoho API Test
          </h1>
          <p className="mt-1 text-sm text-gray-500" data-id="element-2355">
            Temporary page for testing Zoho API calls.
          </p>
        </div>
        <Link to="/api-test/gpt" className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-md hover:bg-purple-700" data-id="element-2356">
          <Sparkles className="h-4 w-4" data-id="element-2357" />
          Test GPT
        </Link>
      </div>

      {/* Module Selector */}
      <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-3" data-id="element-2358">
        <div data-id="element-2359">
          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2360">
            Module
          </label>
          <div className="flex items-center gap-3" data-id="element-2361">
            <select value={selectedModule} onChange={e => setSelectedModule(e.target.value)} className="block w-64 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm" data-id="element-2362">
              {modules.map(mod => <option key={mod.value} value={mod.value} data-id="element-2363">
                  {mod.label}
                </option>)}
            </select>
            {selectedModule === '__custom__' && <input type="text" value={customModule} onChange={e => setCustomModule(e.target.value)} placeholder="Enter module name" className="block w-64 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm" data-id="element-2364" />}
          </div>
        </div>
        <div data-id="element-2365">
          <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-2366">
            Fields Override (leave empty for module defaults)
          </label>
          <input type="text" value={customFields} onChange={e => setCustomFields(e.target.value)} placeholder={effectiveFields || 'e.g. id,Email,First_Name,Last_Name,Tags'} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-xs font-mono" data-id="element-2367" />
          {effectiveFields && !customFields.trim() && <p className="mt-1 text-xs text-gray-400" data-id="element-2368">
              Using defaults: {effectiveFields.substring(0, 80)}...
            </p>}
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-md p-3" data-id="element-2369">
          <p className="text-xs text-amber-800" data-id="element-2370">
            <strong data-id="element-2371">Note:</strong> Mailing_List and Categories are{' '}
            <strong data-id="element-2372">multiselect lookup fields</strong> — they cannot be fetched
            inline. Use the <strong data-id="element-2373">Reverse Lookup</strong> tab to get contacts
            from a specific mailing list or category. It auto-discovers the
            correct API name.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200" data-id="element-2374">
        <nav className="-mb-px flex gap-6" data-id="element-2375">
          {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`py-3 px-1 border-b-2 text-sm font-medium transition-colors ${activeTab === tab.id ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-2376">
              {tab.label}
            </button>)}
        </nav>
      </div>

      {/* Tab 1: List Records */}
      {activeTab === 'list' && <div className="space-y-6" data-id="element-2377">
          <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-3" data-id="element-2378">
            <h2 className="text-sm font-medium text-gray-900" data-id="element-2379">
              Fetch {moduleLabel} List
            </h2>
            <p className="text-xs text-gray-500 font-mono break-all" data-id="element-2380">
              GET {listUrl}
            </p>
            <button onClick={handleFetchList} disabled={loading !== null} className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2381">
              {loading === 'list' ? <>
                  <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" data-id="element-2382" />
                  Fetching...
                </> : `Fetch ${moduleLabel}`}
            </button>
          </div>
          <ResponseBlock response={listResponse} error={listError} onClear={() => {
        setListResponse(null);
        setListError(null);
      }} data-id="element-2383" />
        </div>}

      {/* Tab 2: Record Details */}
      {activeTab === 'details' && <div className="space-y-6" data-id="element-2384">
          <div className="bg-white border border-gray-200 rounded-lg p-5" data-id="element-2385">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2386">
              Record ID
            </label>
            <input type="text" value={recordId} onChange={e => setRecordId(e.target.value)} placeholder={`Enter ${moduleLabel} ID`} className="block w-64 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm" data-id="element-2387" />
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-3" data-id="element-2388">
            <h2 className="text-sm font-medium text-gray-900" data-id="element-2389">
              Fetch {moduleLabel} Details (All Fields)
            </h2>
            <p className="text-xs text-gray-500 font-mono" data-id="element-2390">GET {detailUrl}</p>
            <button onClick={handleFetchDetails} disabled={loading !== null || !recordId.trim()} className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2391">
              {loading === 'detail' ? <>
                  <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" data-id="element-2392" />
                  Fetching...
                </> : `Fetch ${moduleLabel} Details`}
            </button>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-3" data-id="element-2393">
            <h2 className="text-sm font-medium text-gray-900" data-id="element-2394">
              Fetch Related Records
            </h2>
            <p className="text-xs text-gray-500 font-mono" data-id="element-2395">GET {relatedUrl}</p>
            <div className="flex items-center gap-3" data-id="element-2396">
              <select value={relatedModule} onChange={e => setRelatedModule(e.target.value)} className="block w-48 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm" data-id="element-2397">
                {relatedModules.map(mod => <option key={mod.value} value={mod.value} data-id="element-2398">
                    {mod.label}
                  </option>)}
              </select>
              <button onClick={handleFetchRelated} disabled={loading !== null || !recordId.trim()} className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2399">
                {loading === 'related' ? <>
                    <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" data-id="element-2400" />
                    Fetching...
                  </> : 'Fetch Related Records'}
              </button>
            </div>
            <div data-id="element-2401">
              <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-2402">
                Fields (comma-separated)
              </label>
              <input type="text" value={relatedFields} onChange={e => setRelatedFields(e.target.value)} placeholder="ID,First_Name,Last_Name" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm" data-id="element-2403" />
            </div>
          </div>

          <ResponseBlock response={detailResponse} error={detailError} onClear={() => {
        setDetailResponse(null);
        setDetailError(null);
      }} data-id="element-2404" />
        </div>}

      {/* Tab 3: Reverse Lookup (Auto-Discovery) */}
      {activeTab === 'reverse' && <div className="space-y-6" data-id="element-2405">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-2406">
            <p className="text-sm text-blue-800" data-id="element-2407">
              <strong data-id="element-2408">Reverse Lookup with Auto-Discovery</strong> — This
              automatically discovers the correct related list API name (e.g.,
              "Contacts3" instead of "Contacts") by calling the Settings API
              first, then fetches the related records.
            </p>
            <p className="text-xs text-blue-600 mt-2" data-id="element-2409">
              This is exactly how the Campaign Builder finds contacts for
              mailing lists and categories.
            </p>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-4" data-id="element-2410">
            <div className="grid grid-cols-2 gap-4" data-id="element-2411">
              <div data-id="element-2412">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2413">
                  Parent Module
                </label>
                <select value={reverseParentModule} onChange={e => {
              setReverseParentModule(e.target.value);
              setDiscoveredApiName(null);
              setDiscoveryLog('');
            }} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm" data-id="element-2414">
                  <option value="Mailing_Lists" data-id="element-2415">Mailing Lists</option>
                  <option value="Categories" data-id="element-2416">Categories</option>
                  <option value="Tags" data-id="element-2417">Tags</option>
                </select>
              </div>
              <div data-id="element-2418">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2419">
                  Child Module
                </label>
                <select value={reverseChildModule} onChange={e => {
              setReverseChildModule(e.target.value);
              setDiscoveredApiName(null);
              setDiscoveryLog('');
            }} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm" data-id="element-2420">
                  <option value="Contacts" data-id="element-2421">Contacts</option>
                </select>
              </div>
            </div>
            <div data-id="element-2422">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2423">
                Parent Record ID
              </label>
              <input type="text" value={reverseParentId} onChange={e => setReverseParentId(e.target.value)} placeholder="Enter Mailing List / Category ID" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm" data-id="element-2424" />
            </div>

            {discoveredApiName && <div className="bg-green-50 border border-green-200 rounded-md p-3" data-id="element-2425">
                <p className="text-xs text-green-800" data-id="element-2426">
                  <strong data-id="element-2427">Discovered API name:</strong>{' '}
                  <code className="bg-green-100 px-1 rounded" data-id="element-2428">
                    {discoveredApiName}
                  </code>
                </p>
                <p className="text-xs text-green-600 mt-1" data-id="element-2429">
                  URL:{' '}
                  <code className="text-xs" data-id="element-2430">
                    crm/v2/{reverseParentModule}/{reverseParentId.trim()}/
                    {discoveredApiName}
                  </code>
                </p>
              </div>}

            <button onClick={handleDiscoverAndReverseLookup} disabled={loading !== null || !reverseParentId.trim()} className="inline-flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2431">
              {loading === 'reverse' ? <>
                  <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" data-id="element-2432" />
                  Discovering &amp; Fetching...
                </> : `Discover & Get ${reverseChildModule}`}
            </button>
          </div>

          {/* Discovery Log */}
          {discoveryLog && <div className="bg-gray-50 border border-gray-200 rounded-lg p-4" data-id="element-2433">
              <p className="text-xs font-medium text-gray-700 mb-2" data-id="element-2434">
                Discovery Log
              </p>
              <pre className="text-xs text-gray-600 font-mono whitespace-pre-wrap" data-id="element-2435">
                {discoveryLog}
              </pre>
            </div>}

          <ResponseBlock response={reverseResponse} error={reverseError} onClear={() => {
        setReverseResponse(null);
        setReverseError(null);
      }} data-id="element-2436" />
        </div>}

      {/* Tab 4: Settings */}
      {activeTab === 'settings' && <div className="space-y-6" data-id="element-2437">
          <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-3" data-id="element-2438">
            <h2 className="text-sm font-medium text-gray-900" data-id="element-2439">
              Related Lists for {moduleLabel}
            </h2>
            <p className="text-xs text-gray-500 font-mono" data-id="element-2440">
              GET {relatedListsUrl}
            </p>
            <button onClick={handleFetchRelatedLists} disabled={loading !== null} className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2441">
              {loading === 'relatedLists' ? <>
                  <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" data-id="element-2442" />
                  Fetching...
                </> : 'Show Related Lists'}
            </button>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-3" data-id="element-2443">
            <h2 className="text-sm font-medium text-gray-900" data-id="element-2444">
              Fields for {moduleLabel}
            </h2>
            <p className="text-xs text-gray-500 font-mono" data-id="element-2445">GET {fieldsUrl}</p>
            <button onClick={handleFetchFields} disabled={loading !== null} className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2446">
              {loading === 'fields' ? <>
                  <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" data-id="element-2447" />
                  Fetching...
                </> : 'Show Fields'}
            </button>
          </div>

          <ResponseBlock response={settingsResponse} error={settingsError} onClear={() => {
        setSettingsResponse(null);
        setSettingsError(null);
      }} data-id="element-2448" />
        </div>}
    </div>;
}